import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // Verificar token al cargar la aplicación
  useEffect(() => {
    const verifyToken = async () => {
      if (token) {
        try {
          const response = await authAPI.getMe();
          setUser(response.data);
        } catch (error) {
          console.error('Token inválido:', error);
          logout();
        }
      }
      setLoading(false);
    };

    verifyToken();
  }, [token]);

  // Login
  const login = async (email, password) => {
    try {
      const response = await authAPI.login({ email, password });
      
      const { token: newToken, usuario } = response.data;
      
      setToken(newToken);
      setUser(usuario);
      localStorage.setItem('token', newToken);
      
      return { success: true };
    } catch (error) {
      console.error('🔑 AuthContext: Error en login:', error);
      
      // Mensajes de error más específicos
      let errorMessage = 'Error al iniciar sesión';
      
      if (error.response) {
        // Error del servidor
        if (error.response.status === 401) {
          // Verificar si es un error de aprobación pendiente
          if (error.response.data && error.response.data.requiereAprobacion) {
            return { 
              success: false, 
              requiereAprobacion: true,
              error: error.response.data.error
            };
          }
          // Retornar el error completo del servidor para mejor manejo
          // 401 error received from server
          return { 
            success: false, 
            error: error.response.data
          };
        } else if (error.response.status === 404) {
          errorMessage = 'Usuario no encontrado';
        } else if (error.response.data && error.response.data.error) {
          errorMessage = error.response.data.error;
        }
      } else if (error.request) {
        // Error de conexión
        errorMessage = 'Error de conexión. Verifica que el servidor esté funcionando.';
      } else {
        // Otro tipo de error
        errorMessage = error.message || 'Error inesperado';
      }
      
      return { 
        success: false, 
        error: errorMessage
      };
    }
  };

  // Registro
  const register = async (userData) => {
    try {
      const response = await authAPI.register(userData);
      const { token: newToken, usuario } = response.data;
      
      setToken(newToken);
      setUser(usuario);
      localStorage.setItem('token', newToken);
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.message || 'Error al registrar usuario' 
      };
    }
  };

  // Logout
  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
  };

  // Actualizar usuario
  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  // Cambiar contraseña
  const changePassword = async (currentPassword, newPassword) => {
    try {
      await authAPI.changePassword({ currentPassword, newPassword });
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.message || 'Error al cambiar contraseña' 
      };
    }
  };

  // Verificar si el usuario tiene un rol específico
  const hasRole = (role) => {
    return user && user.rol === role;
  };

  // Verificar si el usuario es admin
  const isAdmin = () => {
    return hasRole('ADMIN');
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    updateUser,
    changePassword,
    hasRole,
    isAdmin,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
